/* <tt-carousel> — the one carousel behind every "- TT" section.
   Markup contract:
     [data-track] > [data-slide]      the scroller and its slides
     [data-prev] / [data-next]        arrows, anywhere inside the element
     [data-pagination]                filled with dots or an "n / N" counter
     [data-carousel-ui]               anything hidden when there is nothing to scroll
   Attributes: data-pagination="dots|counter|none", data-loop (arrows wrap).
   Whether the track is a carousel at all is decided in CSS (display: flex). */
(() => {
  if (customElements.get('tt-carousel')) return;

  class TTCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.onClick = this.onClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.update = this.update.bind(this);

      this.addEventListener('click', this.onClick);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      /* where supported, "done gliding" comes from the browser, not a timer */
      this.hasScrollEnd = 'onscrollend' in window;
      if (this.hasScrollEnd) {
        this.track.addEventListener('scrollend', () => {
          this.target = null;
          this.update();
        });
      }
      this.track.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get paginationStyle() {
      return this.dataset.pagination || 'dots';
    }

    get loop() {
      return this.hasAttribute('data-loop');
    }

    get slides() {
      return Array.from(this.track.querySelectorAll(':scope > [data-slide]'));
    }

    get isCarousel() {
      return getComputedStyle(this.track).display === 'flex';
    }

    /* distance from one slide's start to the next */
    get step() {
      const [first, second] = this.slides;
      if (!first) return 0;
      if (second) return second.offsetLeft - first.offsetLeft;
      return first.offsetWidth;
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get stops() {
      const step = this.step;
      if (!step) return 1;
      return Math.round(this.maxScroll / step) + 1;
    }

    get index() {
      const step = this.step;
      if (!step) return 0;
      if (this.track.scrollLeft >= this.maxScroll - 2) return this.stops - 1;
      return Math.round(this.track.scrollLeft / step);
    }

    /* the stop we're on, or heading to — reading scrollLeft mid-glide would
       make a quick double-tap land on the same slide twice */
    get current() {
      return this.target != null ? this.target : this.index;
    }

    onClick(event) {
      const dot = event.target.closest('[data-dot]');
      if (dot && this.contains(dot)) return this.goTo(Number(dot.dataset.dot));
      if (event.target.closest('[data-prev]')) return this.goTo(this.current - 1, true);
      if (event.target.closest('[data-next]')) return this.goTo(this.current + 1, true);
    }

    onKeydown(event) {
      if (!this.isCarousel) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.current - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.current + 1, true);
      }
    }

    onScroll() {
      if (!this.hasScrollEnd) {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => {
          this.target = null;
          this.update();
        }, 150);
      }
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.update();
      });
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      const i = this.slides.indexOf(event.target.closest('[data-slide]'));
      if (i > -1 && this.isCarousel) this.goTo(i);
    }

    goTo(i, fromArrow = false) {
      const stops = this.stops;
      let target = i;
      if (this.loop && fromArrow) {
        target = (i + stops) % stops;
      } else {
        target = Math.max(0, Math.min(i, stops - 1));
      }
      this.target = target;
      this.track.scrollTo({ left: Math.min(target * this.step, this.maxScroll) });
      this.update();
      this.dispatchEvent(new CustomEvent('tt-carousel:change', { detail: { index: target } }));
    }

    renderPagination(index, stops) {
      this.querySelectorAll('[data-pagination]').forEach((el) => {
        if (this.paginationStyle === 'counter') {
          el.textContent = `${index + 1} / ${stops}`;
          return;
        }
        if (this.paginationStyle !== 'dots') return;

        if (el.childElementCount !== stops) {
          el.textContent = '';
          for (let k = 0; k < stops; k++) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'tt-carousel__dot';
            dot.dataset.dot = k;
            dot.setAttribute('aria-label', `Go to slide ${k + 1}`);
            el.appendChild(dot);
          }
        }
        Array.from(el.children).forEach((dot, k) => {
          dot.setAttribute('aria-current', k === index ? 'true' : 'false');
        });
      });
    }

    update() {
      const scrollable = this.isCarousel && this.maxScroll > 2;
      this.querySelectorAll('[data-carousel-ui]').forEach((el) => {
        el.hidden = !scrollable;
      });
      if (!scrollable) return;

      const index = this.current;
      const stops = this.stops;
      this.querySelectorAll('[data-prev]').forEach((b) => (b.disabled = !this.loop && index <= 0));
      this.querySelectorAll('[data-next]').forEach((b) => (b.disabled = !this.loop && index >= stops - 1));
      this.renderPagination(index, stops);
    }
  }

  customElements.define('tt-carousel', TTCarousel);
})();
